import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  EyeIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  StarIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { useProvider } from '../../../hooks/useProviders'
import { useAdminAllDocuments } from '../../../hooks/useDocuments'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { ServiceProvider, DocumentStatus } from '../../../types'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

const serviceTypeLabels: Record<string, string> = {
  // Actual database ServiceType enum values
  AMBULANCE_TRANSPORT: 'Ambulance Transport',
  GHUSL_SERVICE: 'Ghusl Service',
  KAFAN_SERVICE: 'Kafan Service',
  JANAZAH_LEAD: 'Janazah Lead',
  BURIAL_PLOT: 'Burial Plot',
  CATERING: 'Catering',
  FLOWERS: 'Flowers',
  TENT_RENTAL: 'Tent Rental',
  PHOTOGRAPHY: 'Photography',
  YASIN_BOOKS: 'Yasin Books',
  ISLAMIC_SOUVENIRS: 'Islamic Souvenirs',
  // Legacy mappings for backward compatibility
  AMBULANCE_PROVIDER: 'Ambulance',
  USTADZ: 'Ustadz',
  CEMETERY_PROVIDER: 'Cemetery',
  VENDOR: 'Vendor',
  PROVIDER: 'General Provider',
}

const serviceTypeColors: Record<string, string> = {
  // Actual database ServiceType enum values
  AMBULANCE_TRANSPORT: 'bg-red-100 text-red-800',
  GHUSL_SERVICE: 'bg-green-100 text-green-800',
  KAFAN_SERVICE: 'bg-blue-100 text-blue-800',
  JANAZAH_LEAD: 'bg-purple-100 text-purple-800',
  BURIAL_PLOT: 'bg-gray-100 text-gray-800',
  CATERING: 'bg-orange-100 text-orange-800',
  FLOWERS: 'bg-pink-100 text-pink-800',
  TENT_RENTAL: 'bg-yellow-100 text-yellow-800',
  PHOTOGRAPHY: 'bg-indigo-100 text-indigo-800',
  YASIN_BOOKS: 'bg-teal-100 text-teal-800',
  ISLAMIC_SOUVENIRS: 'bg-emerald-100 text-emerald-800',
  // Legacy mappings for backward compatibility
  AMBULANCE_PROVIDER: 'bg-blue-100 text-blue-800',
  USTADZ: 'bg-purple-100 text-purple-800',
  CEMETERY_PROVIDER: 'bg-green-100 text-green-800',
  VENDOR: 'bg-orange-100 text-orange-800',
  PROVIDER: 'bg-gray-100 text-gray-800',
}

const documentTypeLabels: Record<string, string> = {
  BUSINESS_LICENSE: 'Business License',
  PROFESSIONAL_CERTIFICATE: 'Professional Certificate',
  INSURANCE_DOCUMENT: 'Insurance Document',
  IDENTIFICATION: 'Identification Document',
  OPERATING_PERMIT: 'Operating Permit',
  VEHICLE_REGISTRATION: 'Vehicle Registration',
  SERVICE_CERTIFICATION: 'Service Certification',
}

const documentStatusColors: Record<DocumentStatus, string> = {
  UPLOADED: 'bg-yellow-100 text-yellow-800',
  PENDING: 'bg-blue-100 text-blue-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'UPLOADED':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'VERIFIED':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'PENDING':
      return <ClockIcon className="h-4 w-4" />
    case 'VERIFIED':
      return <CheckCircleIcon className="h-4 w-4" />
    default:
      return <DocumentTextIcon className="h-4 w-4" />
  }
}

export default function ActiveProviderDetailsPage() {
  const { providerId } = useParams<{ providerId: string }>()
  const [selectedDocument, setSelectedDocument] = useState<any>(null)
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const [documentPreview, setDocumentPreview] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const { data: provider, isLoading: providerLoading, error: providerError } = useProvider(providerId!)
  const { data: documents = [], isLoading: documentsLoading } = useAdminAllDocuments()

  // Filter documents for this specific provider
  const providerDocuments = documents.filter(doc =>
    doc.serviceProvider?.id === providerId || doc.providerId === providerId
  )

  const isLoading = providerLoading || documentsLoading

  const downloadDocument = async (documentFile: any) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const response = await fetch(`${API_BASE_URL}/api/documents/provider/${documentFile.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = window.document.createElement('a')
      a.href = url
      a.download = documentFile.originalName || documentFile.fileName
      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download document:', error)
      toast.error('Failed to download document')
    }
  }

  const loadDocumentPreview = async (documentFile: any) => {
    setPreviewLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const response = await fetch(`${API_BASE_URL}/api/documents/provider/${documentFile.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': documentFile.mimeType || 'application/octet-stream'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load document preview')
      }

      const blob = await response.blob()
      console.log('Blob type:', blob.type)
      console.log('Blob size:', blob.size)

      // Check if it's an image or PDF
      const fileType = documentFile.mimeType || blob.type
      if (fileType.startsWith('image/')) {
        const url = URL.createObjectURL(blob)
        console.log('Created image blob URL:', url)
        setDocumentPreview(url)
      } else if (fileType === 'application/pdf') {
        const url = URL.createObjectURL(blob)
        console.log('Created PDF blob URL:', url)
        setDocumentPreview(url)
      } else {
        console.log('File type not previewable:', fileType)
        setDocumentPreview(null) // Not previewable
      }
    } catch (error) {
      console.error('Failed to load document preview:', error)
      setDocumentPreview(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleViewDocument = (document: any) => {
    setSelectedDocument(document)
    loadDocumentPreview(document)
    setShowDocumentModal(true)
  }

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false)
    setSelectedDocument(null)
    if (documentPreview) {
      URL.revokeObjectURL(documentPreview)
      setDocumentPreview(null)
    }
  }

  const handleCopyProviderId = async () => {
    if (!provider?.id) return
    try {
      await navigator.clipboard.writeText(provider.id)
      toast.success('Provider ID copied to clipboard')
    } catch (err) {
      console.error('Failed to copy provider ID:', err)
      toast.error('Failed to copy provider ID')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (providerError || !provider) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-medium">Failed to load provider details</div>
        <p className="text-gray-500 mt-2">Please try again later</p>
        <Link
          to="/admin/providers/active"
          className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Active Providers
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/providers/active"
              className="text-primary-600 hover:text-primary-700 font-medium flex items-center"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Active Providers
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Provider Details</h1>
              <p className="text-gray-600 mt-1">View active provider information and documents</p>
            </div>
          </div>
        </div>
      </div>

      {/* Provider Information Cards */}
      <div className="grid grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* User Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">User Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Contact Person</p>
              <p className="text-base text-gray-900">{provider.user.firstName} {provider.user.lastName}</p>
            </div>

            <div className="flex items-center">
              <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-base text-gray-900">{provider.user.email}</p>
              </div>
            </div>

            {provider.user.phone && (
              <div className="flex items-center">
                <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p className="text-base text-gray-900">{provider.user.phone}</p>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500">Account Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  provider.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {provider.verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>

            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500">Registered</p>
                <p className="text-base text-gray-900">
                  {new Date(provider.user.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Provider Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Business Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Business Name</p>
              <p className="text-base text-gray-900">{provider.companyName}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Provider ID</p>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-base text-gray-900 font-mono">{provider.id}</p>
                <button
                  onClick={handleCopyProviderId}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Copy Provider ID"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Service Type</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${serviceTypeColors[provider.serviceType]}`}>
                {serviceTypeLabels[provider.serviceType]}
              </span>
            </div>

            {provider.city && (
              <div className="flex items-center">
                <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="text-base text-gray-900">{provider.city}</p>
                </div>
              </div>
            )}

            {provider.rating && (
              <div className="flex items-center">
                <StarIcon className="h-4 w-4 text-yellow-400 mr-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">Rating</p>
                  <p className="text-base text-gray-900">{provider.rating.toFixed(1)} / 5.0</p>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500">Application Date</p>
                <p className="text-base text-gray-900">
                  {new Date(provider.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      {provider.description && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center mb-4">
            <InformationCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Business Description</h2>
          </div>
          <p className="text-gray-700 whitespace-pre-wrap">
            {provider.description}
          </p>
        </div>
      )}

      {/* Statistics Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center mb-6">
          <ChartBarIcon className="h-5 w-5 text-primary-600 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Performance Statistics</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {provider.responseRate || 0}%
            </div>
            <div className="text-sm text-gray-500">Response Rate</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {provider.averageResponseTime || 0}m
            </div>
            <div className="text-sm text-gray-500">Avg Response Time</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {provider.totalBookings || 0}
            </div>
            <div className="text-sm text-gray-500">Total Bookings</div>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <DocumentIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Documents</h2>
          </div>
        </div>

        {providerDocuments.length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No documents uploaded</p>
          </div>
        ) : (
          <div className="space-y-4">
            {providerDocuments.map((document) => (
              <div
                key={document.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
                        {getStatusIcon(document.status)}
                        <span className="ml-1">{document.status}</span>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800`}>
                        {documentTypeLabels[document.type]}
                      </span>
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {document.originalName}
                      </h3>
                    </div>

                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                      <span>Uploaded {new Date(document.uploadedAt).toLocaleDateString()}</span>
                      <span>File size: {(document.fileSize / 1024).toFixed(1)} KB</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewDocument(document)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <EyeIcon className="h-3 w-3 mr-1" />
                      View
                    </button>
                    <button
                      onClick={() => downloadDocument(document)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl leading-6 font-medium text-gray-900">
                    Document Preview
                  </h3>
                  <button
                    onClick={handleCloseDocumentModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {previewLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner />
                  </div>
                ) : documentPreview ? (
                  <div className="border border-gray-200 rounded-lg p-4">
                    {selectedDocument.mimeType?.startsWith('image/') ? (
                      <img
                        src={documentPreview}
                        alt={selectedDocument.originalName}
                        className="w-full h-auto max-h-96 object-contain"
                      />
                    ) : selectedDocument.mimeType === 'application/pdf' ? (
                      <iframe
                        src={documentPreview}
                        className="w-full h-96"
                        title={selectedDocument.originalName}
                      />
                    ) : (
                      <div className="text-center py-8">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">
                          Preview not available for this file type
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          Please download the file to view it
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      Failed to load preview
                    </p>
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => downloadDocument(selectedDocument)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </button>
                  <button
                    onClick={handleCloseDocumentModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}